"use client";

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  History,
  ExternalLink,
  ArrowRight,
  Send,
  RefreshCw,
  Trash2,
} from "lucide-react";
import {
  getHistoryEntries,
  clearHistory,
  formatRelativeTime,
  truncateAddress,
  HistoryEntry,
} from "@/lib/transaction-history";
import { getExplorerUrl, getChainName } from "@/constants/explorer-urls";

interface HistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function HistoryModal({ isOpen, onClose }: HistoryModalProps) {
  const [entries, setEntries] = useState<HistoryEntry[]>([]);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // Load entries when modal opens
  useEffect(() => {
    if (isOpen) {
      setEntries(getHistoryEntries());
      setShowClearConfirm(false);
    }
  }, [isOpen]);

  const handleClearHistory = () => {
    clearHistory();
    setEntries([]);
    setShowClearConfirm(false);
  };

  const handleViewTx = (entry: HistoryEntry) => {
    const url = getExplorerUrl(entry.chainId, entry.hash);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const getTypeBadge = (type: "transfer" | "swap") => {
    if (type === "transfer") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
          <Send className="h-3 w-3" />
          Sent
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30">
        <RefreshCw className="h-3 w-3" />
        Swapped
      </span>
    );
  };

  const getDescription = (entry: HistoryEntry): string => {
    if (entry.type === "transfer") {
      return `Sent ${entry.fromAmount} ${entry.fromSymbol} to ${truncateAddress(entry.recipient || "")}`;
    }
    // Swap
    return `Swapped ${entry.fromAmount} ${entry.fromSymbol} → ${entry.toAmount || "?"} ${entry.toSymbol || "?"}`;
  };

  const getChainInfo = (entry: HistoryEntry): string => {
    const fromChain = getChainName(entry.chainId);
    if (entry.type === "swap" && entry.toChainId && entry.toChainId !== entry.chainId) {
      const toChain = getChainName(entry.toChainId);
      return `${fromChain} → ${toChain}`;
    }
    return fromChain;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[420px] max-h-[90vh] bg-card border-border p-0 gap-0 overflow-hidden flex flex-col">
        <DialogHeader className="p-6 pb-4 border-b border-border flex-shrink-0">
          <DialogTitle className="text-foreground text-xl font-semibold flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/10">
              <History className="h-5 w-5 text-primary" />
            </div>
            History
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {entries.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted/30 flex items-center justify-center">
                <History className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground text-sm">
                No transactions yet.
              </p>
              <p className="text-muted-foreground text-sm mt-1">
                Your swaps and transfers will appear here.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {entries.map((entry) => (
                <div
                  key={entry.id}
                  className="p-4 hover:bg-muted/10 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        {getTypeBadge(entry.type)}
                      </div>
                      <p className="text-foreground text-sm font-medium truncate">
                        {getDescription(entry)}
                      </p>
                      <p className="text-muted-foreground text-xs mt-1">
                        {getChainInfo(entry)} · {formatRelativeTime(entry.timestamp)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewTx(entry)}
                      className="flex-shrink-0 text-muted-foreground hover:text-foreground"
                    >
                      <span className="text-xs mr-1">View tx</span>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {entries.length > 0 && (
          <div className="p-4 border-t border-border flex-shrink-0">
            {showClearConfirm ? (
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-muted-foreground">Clear all history?</span>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowClearConfirm(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleClearHistory}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowClearConfirm(true)}
                className="w-full text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear history
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
